import * as DialogPrimitive from '@radix-ui/react-dialog';
import { ArrowDownToLine, ArrowUpFromLine, Search, X } from 'lucide-react';
import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';

import { Kbd } from '@/components/ui/kbd';
import { useCustodialPanelState } from '@/features/custodial';
import {
    TokenIcon,
    useTokenPairs,
    useTokenPairStore,
} from '@/features/tokenPairs';
import { cn } from '@/lib/utils';

import { useCommandPaletteState } from '../commandPaletteState';
import {
    selectHeldCoins,
    selectCanClose,
    selectIsRealMode,
    useTradingStore,
} from '../store';

/** Re-exported for existing call sites (header ⌘K hint, Trading.tsx). */
export { useCommandPaletteState };

interface CommandPaletteProps {
    onClosePosition: () => void;
}

interface PaletteItem {
    id: string;
    label: string;
    hint?: string;
    icon: React.ReactNode;
    disabled?: boolean;
    run: () => void;
}

function formatPairDisplay(pair: string): string {
    return pair.replace('-', '/').toUpperCase();
}

/** Stable identity so the closed-palette memo below never invalidates. */
const EMPTY_ITEMS: PaletteItem[] = [];

/**
 * ⌘K command palette: switch markets, jump to deposit/withdraw, close the
 * active position. Registered globally; renders nothing until opened.
 */
const CommandPalette: React.FC<CommandPaletteProps> = ({ onClosePosition }) => {
    const open = useCommandPaletteState((s) => s.open);
    const setOpen = useCommandPaletteState((s) => s.setOpen);
    const [query, setQuery] = useState('');
    const [highlighted, setHighlighted] = useState(0);
    const listRef = useRef<HTMLDivElement>(null);

    const { selectedPair, setSelectedPair, networkId } = useTokenPairStore();
    const { data } = useTokenPairs(networkId);

    const heldCoins = useTradingStore(selectHeldCoins);
    const canClose = useTradingStore(selectCanClose);
    const isRealMode = useTradingStore(selectIsRealMode);
    const openDeposit = useCustodialPanelState((s) => s.openDeposit);
    const openWithdraw = useCustodialPanelState((s) => s.openWithdraw);

    // Global ⌘K / Ctrl+K listener.
    useEffect(() => {
        const onKeyDown = (e: KeyboardEvent) => {
            if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
                e.preventDefault();
                const s = useCommandPaletteState.getState();
                s.setOpen(!s.open);
            }
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, []);

    // Reset transient state whenever the palette opens.
    useEffect(() => {
        if (open) {
            setQuery('');
            setHighlighted(0);
        }
    }, [open]);

    const close = useCallback(() => setOpen(false), [setOpen]);

    const items = useMemo<PaletteItem[]>(() => {
        // Nothing is visible while the palette is closed, and several deps here
        // (notably `onClosePosition`, which is rebuilt on every Trading render)
        // change at the price-tick rate — so without this guard the whole
        // ~230-market list, with a TokenIcon element and three closures per
        // row, is reconstructed dozens of times per second for nothing.
        if (!open) return EMPTY_ITEMS;

        const q = query.trim().toLowerCase();

        const markets: PaletteItem[] = (data?.symbols ?? [])
            .filter((pair) => pair !== selectedPair)
            .filter((pair) => !q || pair.replace('-', '/').includes(q))
            .map((pair) => ({
                id: `market-${pair}`,
                label: formatPairDisplay(pair),
                // Never disabled by an open position: one position per coin is
                // allowed, so ⌘K is the fastest way to the next market. A market
                // already held says so instead.
                hint: heldCoins.has((pair.split('-')[0] ?? '').toUpperCase())
                    ? 'Position open'
                    : 'Market',
                icon: <TokenIcon token={pair.split('-')[0] ?? ''} size="sm" />,
                run: () => {
                    setSelectedPair(pair);
                    close();
                },
            }));

        const actions: PaletteItem[] = [];
        if (canClose) {
            actions.push({
                id: 'close-position',
                label: 'Close position',
                // No key hint: closing is off the keyboard, so the palette (or
                // the button) is the whole way to do it.
                icon: <X size={14} className="text-destructive" />,
                run: () => {
                    onClosePosition();
                    close();
                },
            });
        }
        if (isRealMode) {
            actions.push(
                {
                    id: 'deposit',
                    label: 'Deposit USDC',
                    hint: 'Wallet',
                    icon: (
                        <ArrowDownToLine size={14} className="text-primary" />
                    ),
                    run: () => {
                        openDeposit();
                        close();
                    },
                },
                {
                    id: 'withdraw',
                    label: 'Withdraw USDC',
                    hint: 'Wallet',
                    icon: (
                        <ArrowUpFromLine
                            size={14}
                            className="text-muted-foreground"
                        />
                    ),
                    run: () => {
                        openWithdraw();
                        close();
                    },
                },
            );
        }

        const visibleActions = actions.filter(
            (a) => !q || a.label.toLowerCase().includes(q),
        );

        return [...visibleActions, ...markets];
    }, [
        open,
        data,
        query,
        selectedPair,
        heldCoins,
        canClose,
        isRealMode,
        setSelectedPair,
        onClosePosition,
        openDeposit,
        openWithdraw,
        close,
    ]);

    const enabledIndexes = useMemo(
        () =>
            items
                .map((item, i) => (item.disabled ? -1 : i))
                .filter((i) => i >= 0),
        [items],
    );

    // Keep the highlight on an enabled row as the list refilters.
    useEffect(() => {
        if (!enabledIndexes.includes(highlighted)) {
            setHighlighted(enabledIndexes[0] ?? 0);
        }
    }, [enabledIndexes, highlighted]);

    const moveHighlight = (direction: 1 | -1) => {
        if (enabledIndexes.length === 0) return;
        const pos = enabledIndexes.indexOf(highlighted);
        const next =
            enabledIndexes[
                (pos + direction + enabledIndexes.length) %
                    enabledIndexes.length
            ];
        setHighlighted(next ?? 0);
        listRef.current
            ?.querySelector(`[data-index="${next}"]`)
            ?.scrollIntoView({ block: 'nearest' });
    };

    const handleInputKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            moveHighlight(1);
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            moveHighlight(-1);
        } else if (e.key === 'Enter') {
            e.preventDefault();
            const item = items[highlighted];
            if (item && !item.disabled) item.run();
        }
    };

    return (
        <DialogPrimitive.Root open={open} onOpenChange={setOpen}>
            <DialogPrimitive.Portal>
                <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/50 backdrop-blur-[2px]" />
                <DialogPrimitive.Content
                    className="fixed left-1/2 top-[18%] z-50 w-[min(560px,calc(100vw-32px))] -translate-x-1/2 overflow-hidden rounded-xl border border-border bg-popover shadow-[0_8px_32px_rgba(0,0,0,0.5)]"
                    aria-describedby={undefined}
                >
                    <DialogPrimitive.Title className="sr-only">
                        Command palette
                    </DialogPrimitive.Title>
                    <div className="flex items-center gap-2 border-b border-border px-3 focus-within:border-ring">
                        <Search
                            size={14}
                            className="shrink-0 text-muted-foreground"
                        />
                        <input
                            autoFocus
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            onKeyDown={handleInputKeyDown}
                            role="combobox"
                            aria-expanded="true"
                            aria-controls="command-palette-list"
                            aria-activedescendant={
                                items[highlighted] &&
                                !items[highlighted].disabled
                                    ? `command-palette-item-${highlighted}`
                                    : undefined
                            }
                            aria-label="Search markets and actions"
                            placeholder="Switch market or run an action…"
                            className="h-11 w-full border-0 bg-transparent p-0 text-sm text-foreground outline-none placeholder:text-muted-foreground focus:border-0 focus:outline-none focus:ring-0"
                        />
                        <Kbd>esc</Kbd>
                    </div>
                    <div
                        ref={listRef}
                        id="command-palette-list"
                        role="listbox"
                        aria-label="Results"
                        className="max-h-[320px] overflow-y-auto p-1.5"
                    >
                        {items.length === 0 && (
                            <div
                                role="status"
                                className="px-3 py-6 text-center text-xs text-muted-foreground"
                            >
                                Nothing matches "{query}".
                            </div>
                        )}
                        {items.map((item, i) => (
                            <button
                                key={item.id}
                                type="button"
                                id={`command-palette-item-${i}`}
                                role="option"
                                aria-selected={i === highlighted}
                                data-index={i}
                                disabled={item.disabled}
                                onClick={() => item.run()}
                                onMouseMove={() =>
                                    !item.disabled && setHighlighted(i)
                                }
                                className={cn(
                                    'flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-left text-sm',
                                    item.disabled
                                        ? 'cursor-not-allowed opacity-40'
                                        : 'cursor-pointer',
                                    i === highlighted &&
                                        !item.disabled &&
                                        'bg-secondary text-foreground inset-ring inset-ring-ring/60',
                                )}
                            >
                                <span className="flex w-5 shrink-0 items-center justify-center">
                                    {item.icon}
                                </span>
                                <span className="font-medium">
                                    {item.label}
                                </span>
                                {item.hint && (
                                    <span className="ml-auto text-[10px] text-muted-foreground">
                                        {item.hint}
                                    </span>
                                )}
                            </button>
                        ))}
                    </div>
                </DialogPrimitive.Content>
            </DialogPrimitive.Portal>
        </DialogPrimitive.Root>
    );
};

export default CommandPalette;
